/** Una base de datos: sus vistas, filtros, orden y propiedades, y la vista activa debajo.
 *
 * El esquema (propiedades y vistas) se guarda en la base con una espera, igual que el texto de
 * una página; los valores de cada fila se guardan al momento, uno a uno. Todo se pinta antes
 * de que conteste el servidor y, si falla, se vuelve a pedir lo que había.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ErrorDeApi, api } from '../../api'
import { Cargando } from '../../componentes/piezas'
import { usePortal } from '../../contexto'
import { useEspacio } from '../contexto'
import { Flotante, Menu } from '../Menu'
import type { Pagina } from '../tipos'
import { Calendario } from './Calendario'
import { Galeria } from './Galeria'
import { aplicarVista, conOpcionNueva, idCorto, operadoresDe, propiedadDe, propiedadNueva } from './logica'
import { Tabla } from './Tabla'
import { Tablero } from './Tablero'
import {
  ICONO_DE_TIPO, TIPOS, VISTAS, type Esquema, type Fila, type Filtro, type TipoProp, type TipoVista,
  type Vista,
} from './tipos'

export interface AccionesDeVista {
  esquema: Esquema
  vista: Vista
  filas: Fila[]
  editable: boolean
  alCambiarFila: (id: number, cambios: { props?: Record<string, unknown>; title?: string; position?: number }) => void
  alNuevaFila: (props?: Record<string, unknown>) => void
  alBorrarFila: (id: number) => void
  alAbrir: (id: number) => void
  alCrearOpcion: (propId: string, nombre: string) => string | null
  alCambiarEsquema: (esquema: Esquema) => void
  alCambiarVista: (vista: Vista) => void
}

export default function BaseVista({ base }: { base: Pagina }) {
  const { ruta, servidor, avisar } = usePortal()
  const navegar = useNavigate()
  const editable = base.puede_editar
  const rutaBase = `${ruta}/paginas/${base.id}`

  const [esquema, setEsquema] = useState<Esquema>(base.db as unknown as Esquema)
  const [filas, setFilas] = useState<Fila[] | null>(null)
  const [vistaId, setVistaId] = useState<string>(() => {
    try {
      return localStorage.getItem(`bd.${base.id}.vista`) ?? ''
    } catch {
      return ''
    }
  })
  const [panel, setPanel] = useState<'filtros' | 'orden' | 'propiedades' | 'nueva-vista' | null>(null)
  const [menuVista, setMenuVista] = useState(false)
  const [renombrando, setRenombrando] = useState(false)

  const botones = {
    filtros: useRef<HTMLButtonElement>(null),
    orden: useRef<HTMLButtonElement>(null),
    propiedades: useRef<HTMLButtonElement>(null),
    nuevaVista: useRef<HTMLButtonElement>(null),
    menuVista: useRef<HTMLButtonElement>(null),
  }

  const fallo = useCallback(
    (e: unknown, texto: string) => avisar(e instanceof ErrorDeApi ? e.message : texto, 'malo'),
    [avisar],
  )

  const cargarFilas = useCallback(async () => {
    try {
      setFilas(await api.get<Fila[]>(`${rutaBase}/filas`))
    } catch (e) {
      fallo(e, 'No se han podido cargar las filas.')
      setFilas([])
    }
  }, [rutaBase, fallo])

  useEffect(() => {
    void cargarFilas()
  }, [cargarFilas])

  const vista = esquema.views.find((v) => v.id === vistaId) ?? esquema.views[0]

  // ── Esquema: se guarda con espera ──
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendiente = useRef<Esquema | null>(null)
  // El guardado del esquema que está viajando ahora: un valor de fila que use una opción recién
  // creada tiene que esperar a que ésta exista en el servidor, o éste lo rechazaría.
  const enVuelo = useRef<Promise<void>>(Promise.resolve())

  const vaciarEsquema = useCallback(async () => {
    if (temporizador.current) clearTimeout(temporizador.current)
    temporizador.current = null
    const quedan = pendiente.current
    if (!quedan) return
    pendiente.current = null
    const envio = (async () => {
      try {
        await api.patch(rutaBase, { db: quedan })
      } catch (e) {
        fallo(e, 'No se han podido guardar los cambios de la base.')
        try {
          const fresca = await api.get<Pagina>(rutaBase)
          setEsquema(fresca.db as unknown as Esquema)
        } catch { /* sin conexión: se queda lo que hay */ }
      }
    })()
    enVuelo.current = envio
    await envio
  }, [rutaBase, fallo])

  useEffect(() => () => void vaciarEsquema(), [vaciarEsquema])

  const cambiarEsquema = useCallback(
    (nuevo: Esquema) => {
      setEsquema(nuevo)
      pendiente.current = nuevo
      if (temporizador.current) clearTimeout(temporizador.current)
      temporizador.current = setTimeout(() => void vaciarEsquema(), 600)
    },
    [vaciarEsquema],
  )

  const cambiarVista = (nueva: Vista) =>
    cambiarEsquema({ ...esquema, views: esquema.views.map((v) => (v.id === nueva.id ? nueva : v)) })

  const elegirVista = (id: string) => {
    setVistaId(id)
    try {
      localStorage.setItem(`bd.${base.id}.vista`, id)
    } catch { /* comodidad por navegador */ }
  }

  // ── Filas ──
  const cambiarFila: AccionesDeVista['alCambiarFila'] = useCallback(
    (id, cambios) => {
      setFilas((previas) =>
        (previas ?? []).map((f) => {
          if (f.id !== id) return f
          const props = { ...f.props }
          for (const [k, v] of Object.entries(cambios.props ?? {})) {
            if (v == null) delete props[k]
            else props[k] = v
          }
          return {
            ...f, props,
            title: cambios.title ?? f.title,
            position: cambios.position ?? f.position,
          }
        }),
      )
      void (async () => {
        try {
          // Si hay un cambio de esquema sin enviar (una opción nueva, por ejemplo), va primero.
          if (pendiente.current) await vaciarEsquema()
          await enVuelo.current
          await api.patch(`${ruta}/paginas/${id}`, cambios)
        } catch (e) {
          fallo(e, 'No se ha podido guardar el cambio.')
          void cargarFilas()
        }
      })()
    },
    [ruta, fallo, cargarFilas, vaciarEsquema],
  )

  const nuevaFila: AccionesDeVista['alNuevaFila'] = useCallback(
    async (props) => {
      try {
        await api.post<Pagina>(`${rutaBase}/filas`, { title: '', props: props ?? {} })
        // Se vuelve a pedir la lista para tener la posición que le ha dado el servidor.
        await cargarFilas()
      } catch (e) {
        fallo(e, 'No se ha podido añadir la fila.')
      }
    },
    [rutaBase, fallo, cargarFilas],
  )

  const borrarFila = useCallback(
    (id: number) => {
      setFilas((previas) => (previas ?? []).filter((f) => f.id !== id))
      api.borrar(`${ruta}/paginas/${id}`).then(
        () => avisar('La fila está en la papelera.'),
        (e) => {
          fallo(e, 'No se ha podido borrar la fila.')
          void cargarFilas()
        },
      )
    },
    [ruta, avisar, fallo, cargarFilas],
  )

  const crearOpcion = useCallback(
    (propId: string, nombre: string) => {
      const r = conOpcionNueva(esquema, propId, nombre)
      cambiarEsquema(r.esquema)
      return r.opcionId
    },
    [esquema, cambiarEsquema],
  )

  const visibles = useMemo(
    () => (filas && vista ? aplicarVista(filas, vista, esquema) : []),
    [filas, vista, esquema],
  )

  if (!vista) return null

  const acciones: AccionesDeVista = {
    esquema, vista, filas: visibles, editable,
    alCambiarFila: cambiarFila, alNuevaFila: nuevaFila, alBorrarFila: borrarFila,
    alAbrir: (id) => navegar(`/${servidor.id}/paginas/${id}`),
    alCrearOpcion: crearOpcion, alCambiarEsquema: cambiarEsquema, alCambiarVista: cambiarVista,
  }

  const nuevaVista = (tipo: TipoVista) => {
    const nombre = VISTAS.find((v) => v.tipo === tipo)!.nombre
    const v: Vista = { id: idCorto(), name: nombre, type: tipo }
    if (tipo === 'board') {
      v.group_by = esquema.properties.find((p) => p.type === 'status' || p.type === 'select')?.id
    }
    if (tipo === 'calendar') v.date_by = esquema.properties.find((p) => p.type === 'date')?.id
    cambiarEsquema({ ...esquema, views: [...esquema.views, v] })
    elegirVista(v.id)
  }

  const nFiltros = vista.filters?.length ?? 0

  return (
    <div className="bd">
      <div className="bd__barra">
        <div className="bd__vistas" role="tablist">
          {esquema.views.map((v) =>
            v.id === vista.id && renombrando ? (
              <input
                key={v.id}
                autoFocus
                className="bd__renombrar"
                defaultValue={v.name}
                onBlur={(e) => {
                  setRenombrando(false)
                  const nombre = e.target.value.trim()
                  if (nombre && nombre !== v.name) cambiarVista({ ...v, name: nombre })
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur()
                }}
                aria-label="Nombre de la vista"
              />
            ) : (
              <button
                key={v.id}
                role="tab"
                aria-selected={v.id === vista.id}
                className={`bd__vista${v.id === vista.id ? ' bd__vista--activa' : ''}`}
                onClick={() => elegirVista(v.id)}
                onDoubleClick={() => editable && v.id === vista.id && setRenombrando(true)}
              >
                <span aria-hidden="true">{VISTAS.find((x) => x.tipo === v.type)?.icono}</span> {v.name}
              </button>
            ),
          )}
          {editable && (
            <>
              <button ref={botones.menuVista} className="bd__icono" onClick={() => setMenuVista((x) => !x)}
                      aria-label="Opciones de la vista" title="Opciones de la vista">⋯</button>
              <button ref={botones.nuevaVista} className="bd__icono"
                      onClick={() => setPanel(panel === 'nueva-vista' ? null : 'nueva-vista')}
                      aria-label="Añadir vista" title="Añadir vista">＋</button>
            </>
          )}
        </div>

        <div className="bd__herramientas">
          <button ref={botones.filtros}
                  className={`bd__herramienta${nFiltros ? ' bd__herramienta--activa' : ''}`}
                  onClick={() => setPanel(panel === 'filtros' ? null : 'filtros')}>
            Filtrar{nFiltros ? ` · ${nFiltros}` : ''}
          </button>
          <button ref={botones.orden}
                  className={`bd__herramienta${vista.sort ? ' bd__herramienta--activa' : ''}`}
                  onClick={() => setPanel(panel === 'orden' ? null : 'orden')}>
            Ordenar{vista.sort ? ' · 1' : ''}
          </button>
          <button ref={botones.propiedades} className="bd__herramienta"
                  onClick={() => setPanel(panel === 'propiedades' ? null : 'propiedades')}>
            Propiedades
          </button>
          {editable && (
            <button className="boton boton--principal bd__nueva" onClick={() => void nuevaFila()}>
              Nueva
            </button>
          )}
        </div>
      </div>

      {menuVista && (
        <Menu
          ancla={botones.menuVista}
          alCerrar={() => setMenuVista(false)}
          opciones={[
            { texto: 'Renombrar vista', icono: '✎', alElegir: () => setRenombrando(true) },
            ...(esquema.views.length > 1
              ? [{
                  texto: 'Eliminar vista', icono: '🗑', peligro: true,
                  alElegir: () => {
                    const resto = esquema.views.filter((v) => v.id !== vista.id)
                    cambiarEsquema({ ...esquema, views: resto })
                    elegirVista(resto[0].id)
                  },
                }]
              : []),
          ]}
        />
      )}
      {panel === 'nueva-vista' && (
        <Menu
          ancla={botones.nuevaVista}
          alCerrar={() => setPanel(null)}
          opciones={VISTAS.map((v) => ({
            texto: v.nombre, icono: v.icono, alElegir: () => nuevaVista(v.tipo),
          }))}
        />
      )}
      {panel === 'filtros' && (
        <PanelDeFiltros ancla={botones.filtros} esquema={esquema} vista={vista}
                        alCambiar={cambiarVista} alCerrar={() => setPanel(null)} />
      )}
      {panel === 'orden' && (
        <PanelDeOrden ancla={botones.orden} esquema={esquema} vista={vista}
                      alCambiar={cambiarVista} alCerrar={() => setPanel(null)} />
      )}
      {panel === 'propiedades' && (
        <PanelDePropiedades ancla={botones.propiedades} esquema={esquema} vista={vista}
                            editable={editable} alCambiarVista={cambiarVista}
                            alCambiarEsquema={cambiarEsquema} alCerrar={() => setPanel(null)} />
      )}

      {filas === null ? (
        <Cargando filas={4} />
      ) : vista.type === 'table' ? (
        <Tabla {...acciones} />
      ) : vista.type === 'board' ? (
        <Tablero {...acciones} todas={filas} />
      ) : vista.type === 'calendar' ? (
        <Calendario {...acciones} />
      ) : (
        <Galeria {...acciones} />
      )}
    </div>
  )
}

// ───────────────────────────── Paneles ─────────────────────────────

type RefBoton = React.RefObject<HTMLElement | null>

function PanelDeFiltros({ ancla, esquema, vista, alCambiar, alCerrar }: {
  ancla: RefBoton; esquema: Esquema; vista: Vista; alCambiar: (v: Vista) => void; alCerrar: () => void
}) {
  const filtros = vista.filters ?? []
  const poner = (lista: Filtro[]) => alCambiar({ ...vista, filters: lista.length ? lista : undefined })
  const cambiar = (i: number, f: Filtro) => poner(filtros.map((x, j) => (j === i ? f : x)))

  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={460}>
      <div className="panel">
        {filtros.length === 0 && <div className="panel__vacio">Sin filtros: se ven todas las filas.</div>}
        {filtros.map((f, i) => {
          const prop = f.prop === 'title' ? ('title' as const) : propiedadDe(esquema, f.prop)
          const ops = prop ? operadoresDe(prop) : []
          const op = ops.find((o) => o.op === f.op) ?? ops[0]
          return (
            <div className="panel__fila" key={i}>
              <select value={f.prop} aria-label="Propiedad"
                      onChange={(e) => {
                        const nueva = e.target.value === 'title' ? 'title' : propiedadDe(esquema, e.target.value)!
                        cambiar(i, { prop: e.target.value, op: operadoresDe(nueva)[0].op, value: null })
                      }}>
                <option value="title">Nombre</option>
                {esquema.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={op?.op} aria-label="Condición"
                      onChange={(e) => cambiar(i, { ...f, op: e.target.value })}>
                {ops.map((o) => <option key={o.op} value={o.op}>{o.nombre}</option>)}
              </select>
              {!op?.sinValor && (
                <ValorDeFiltro prop={prop} valor={f.value}
                               alCambiar={(valor) => cambiar(i, { ...f, value: valor })} />
              )}
              <button className="arbol-boton arbol-boton--visible" onClick={() => poner(filtros.filter((_, j) => j !== i))}
                      aria-label="Quitar filtro">✕</button>
            </div>
          )
        })}
        <button className="boton boton--plano panel__añadir"
                onClick={() => poner([...filtros, { prop: 'title', op: 'contains', value: null }])}>
          ＋ Añadir filtro
        </button>
      </div>
    </Flotante>
  )
}

function ValorDeFiltro({ prop, valor, alCambiar }: {
  prop: ReturnType<typeof propiedadDe> | 'title'; valor: Filtro['value']
  alCambiar: (v: Filtro['value']) => void
}) {
  const { personas } = useEspacio()
  if (prop && prop !== 'title' && prop.type === 'person') {
    return (
      <select value={String(valor ?? '')} onChange={(e) => alCambiar(e.target.value || null)} aria-label="Valor">
        <option value="">Elige…</option>
        {personas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
      </select>
    )
  }
  if (prop && prop !== 'title' && (prop.type === 'select' || prop.type === 'status' || prop.type === 'multi_select')) {
    return (
      <select value={String(valor ?? '')} onChange={(e) => alCambiar(e.target.value || null)} aria-label="Valor">
        <option value="">Elige…</option>
        {prop.options?.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    )
  }
  const tipo = prop && prop !== 'title' ? (prop.type === 'date' ? 'date' : prop.type === 'number' ? 'number' : 'text') : 'text'
  return (
    <input type={tipo} value={String(valor ?? '')} aria-label="Valor" placeholder="Valor"
           onChange={(e) => alCambiar(tipo === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value || null)} />
  )
}

function PanelDeOrden({ ancla, esquema, vista, alCambiar, alCerrar }: {
  ancla: RefBoton; esquema: Esquema; vista: Vista; alCambiar: (v: Vista) => void; alCerrar: () => void
}) {
  const orden = vista.sort
  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={340}>
      <div className="panel">
        <div className="panel__fila">
          <select value={orden?.prop ?? ''} aria-label="Ordenar por"
                  onChange={(e) => alCambiar({ ...vista, sort: e.target.value ? { prop: e.target.value, dir: orden?.dir ?? 'asc' } : undefined })}>
            <option value="">Sin orden (el manual)</option>
            <option value="title">Nombre</option>
            {esquema.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {orden && (
            <select value={orden.dir} aria-label="Sentido"
                    onChange={(e) => alCambiar({ ...vista, sort: { ...orden, dir: e.target.value as 'asc' | 'desc' } })}>
              <option value="asc">Ascendente</option>
              <option value="desc">Descendente</option>
            </select>
          )}
        </div>
      </div>
    </Flotante>
  )
}

function PanelDePropiedades({ ancla, esquema, vista, editable, alCambiarVista, alCambiarEsquema, alCerrar }: {
  ancla: RefBoton; esquema: Esquema; vista: Vista; editable: boolean
  alCambiarVista: (v: Vista) => void; alCambiarEsquema: (e: Esquema) => void; alCerrar: () => void
}) {
  const [nombre, setNombre] = useState('')
  const [tipo, setTipo] = useState<TipoProp>('text')
  const ocultas = new Set(vista.hidden ?? [])

  const alternar = (id: string) => {
    const nuevas = new Set(ocultas)
    if (nuevas.has(id)) nuevas.delete(id)
    else nuevas.add(id)
    alCambiarVista({ ...vista, hidden: nuevas.size ? [...nuevas] : undefined })
  }

  const añadir = () => {
    if (!nombre.trim()) return
    alCambiarEsquema({ ...esquema, properties: [...esquema.properties, propiedadNueva(nombre, tipo)] })
    setNombre('')
  }

  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={320}>
      <div className="panel">
        <div className="panel__titulo">Propiedades visibles</div>
        {esquema.properties.length === 0 && <div className="panel__vacio">Todavía no hay propiedades.</div>}
        {esquema.properties.map((p) => (
          <label className="panel__propiedad" key={p.id}>
            <input type="checkbox" checked={!ocultas.has(p.id)} onChange={() => alternar(p.id)} />
            <span aria-hidden="true" className="panel__tipo">{ICONO_DE_TIPO[p.type]}</span>
            {p.name}
          </label>
        ))}
        {editable && (
          <div className="panel__nueva">
            <div className="panel__titulo">Nueva propiedad</div>
            <div className="panel__fila">
              <input value={nombre} placeholder="Nombre" aria-label="Nombre de la propiedad"
                     onChange={(e) => setNombre(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && añadir()} />
              <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoProp)} aria-label="Tipo">
                {TIPOS.map((t) => <option key={t.tipo} value={t.tipo}>{t.nombre}</option>)}
              </select>
              <button className="boton" onClick={añadir} disabled={!nombre.trim()}>Añadir</button>
            </div>
          </div>
        )}
      </div>
    </Flotante>
  )
}
